import React, { useState } from 'react';
import { Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const Subscribe = ({ variant = 'default' }) => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);

    // IMPORTANT: Replace this with your actual Mailchimp form action URL
    // Get it from: Mailchimp Dashboard -> Audience -> Signup Forms -> Embedded Forms
    // Look for the action URL in the form code
    const MAILCHIMP_URL = 'YOUR_MAILCHIMP_FORM_ACTION_URL';

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email || !email.includes('@')) {
            toast.error('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);

        try {
            // For static sites, we'll use Mailchimp's embedded form submission
            // This opens a new window/tab for the Mailchimp confirmation
            if (MAILCHIMP_URL !== 'YOUR_MAILCHIMP_FORM_ACTION_URL') {
                // Create a form and submit to Mailchimp
                const form = document.createElement('form');
                form.action = MAILCHIMP_URL;
                form.method = 'POST';
                form.target = '_blank';
                
                const emailInput = document.createElement('input');
                emailInput.type = 'email';
                emailInput.name = 'EMAIL';
                emailInput.value = email;
                form.appendChild(emailInput);
                
                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
            }
            
            // Show success state
            setIsSubscribed(true);
            toast.success('Thank you for subscribing!');
            setEmail('');
            
            // Reset after 5 seconds
            setTimeout(() => setIsSubscribed(false), 5000);
        } catch (error) {
            console.error('Subscription error:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (variant === 'footer') {
        return (
            <div className="w-full">
                <h4 className="font-heading text-lg text-[#4A4E4D] mb-3">Stay Updated</h4>
                <p className="text-[#4A4E4D]/60 text-sm mb-4">
                    Get the latest spa deals and wellness tips delivered to your inbox.
                </p>
                {isSubscribed ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2 text-[#722F37]"
                    >
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">You're subscribed!</span>
                    </motion.div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex gap-2">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-2 bg-white border border-[#E0DAD0] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#C8A86B]/30"
                            data-testid="subscribe-email-footer"
                        />
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-[#722F37] text-white rounded-full text-sm font-medium hover:bg-[#8B3D47] transition-colors disabled:opacity-50"
                            data-testid="subscribe-btn-footer"
                        >
                            {isSubmitting ? '...' : 'Subscribe'}
                        </button>
                    </form>
                )}
            </div>
        );
    }

    // Default banner variant
    return (
        <section className="bg-gradient-to-r from-[#F5E1E4] via-[#F9F6F0] to-[#F5E1E4] py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="w-16 h-16 mx-auto mb-6 bg-[#722F37]/10 rounded-full flex items-center justify-center">
                        <Mail className="w-8 h-8 text-[#722F37]" />
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D] mb-4">
                        Stay in the Loop
                    </h2>
                    <p className="text-[#4A4E4D]/70 text-lg mb-8 max-w-xl mx-auto">
                        Subscribe to receive exclusive spa deals, wellness tips, and new spa openings across India.
                    </p>

                    {isSubscribed ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex items-center justify-center gap-3 text-[#722F37]"
                        >
                            <CheckCircle className="w-6 h-6" />
                            <span className="text-lg font-medium">Welcome! You're now subscribed.</span>
                        </motion.div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                            <div className="relative flex-1">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4E4D]/40" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    className="w-full pl-12 pr-4 py-4 bg-white border border-[#E0DAD0] rounded-full text-[#4A4E4D] placeholder:text-[#4A4E4D]/40 focus:outline-none focus:ring-2 focus:ring-[#722F37]/30 shadow-sm"
                                    data-testid="subscribe-email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#722F37] text-white rounded-full font-medium hover:bg-[#8B3D47] transition-all duration-300 disabled:opacity-50 shadow-md hover:shadow-lg"
                                data-testid="subscribe-btn"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Subscribing...
                                    </>
                                ) : (
                                    <>
                                        Subscribe
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    <p className="text-[#4A4E4D]/50 text-sm mt-6">
                        No spam, unsubscribe anytime. We respect your privacy.
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default Subscribe;
